/**
 * Locales List View
 *
 * Language locales (BCP 47) management page with default voice configuration
 *
 * Story 11.17 - Consolidated voice configuration to locales
 */

import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  useLocales,
  useCreateLocale,
  useUpdateLocale,
  useDeleteLocale,
  useLocalesWithVoice,
  useAvailableVoices,
  useSetDefaultVoice,
} from '../services/localesApi';
import { useLanguages } from '../services/languagesApi';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import type {
  LocaleWithVoiceCount,
  LocaleWithDefaultVoice,
  CreateLocaleRequest,
  UpdateLocaleRequest,
} from '../types/locale.types';

interface EditFormState {
  name: string;
  order: number;
}

interface CreateFormState {
  localeCode: string;
  languageCode: string;
  name: string;
  order: number;
}

export function LocalesListView() {
  const { data: locales, isLoading, error } = useLocales();
  const { data: localesWithVoice } = useLocalesWithVoice();
  const { data: languages } = useLanguages();
  const createLocaleMutation = useCreateLocale();
  const updateLocaleMutation = useUpdateLocale();
  const deleteLocaleMutation = useDeleteLocale();
  const setDefaultVoiceMutation = useSetDefaultVoice();

  const [editingLocale, setEditingLocale] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({ name: '', order: 1 });
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>({
    localeCode: '',
    languageCode: '',
    name: '',
    order: 1,
  });

  // Voice editing state
  const [editingVoiceLocale, setEditingVoiceLocale] = useState<string | null>(null);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');

  // Fetch available voices for the locale being edited
  const { data: availableVoices, isLoading: isLoadingVoices } = useAvailableVoices(
    editingVoiceLocale || '',
    !!editingVoiceLocale
  );

  // Get default voice for a locale from localesWithVoice data
  const getDefaultVoice = (localeCode: string): LocaleWithDefaultVoice | undefined => {
    return localesWithVoice?.find((l) => l.localeCode === localeCode);
  };

  const handleEditClick = (locale: LocaleWithVoiceCount) => {
    setEditingLocale(locale.localeCode);
    setEditForm({ name: locale.name, order: locale.order });
  };

  const handleCancelEdit = () => {
    setEditingLocale(null);
    setEditForm({ name: '', order: 1 });
  };

  const handleEditVoiceClick = (locale: LocaleWithVoiceCount) => {
    const localeVoice = getDefaultVoice(locale.localeCode);
    setEditingVoiceLocale(locale.localeCode);
    setSelectedVoiceId(localeVoice?.defaultVoice?.id ? String(localeVoice.defaultVoice.id) : '');
  };

  const handleCancelVoiceEdit = () => {
    setEditingVoiceLocale(null);
    setSelectedVoiceId('');
  };

  const handleSaveVoice = async (localeCode: string) => {
    try {
      await setDefaultVoiceMutation.mutateAsync({
        localeCode,
        request: { voiceId: selectedVoiceId ? Number(selectedVoiceId) : null },
      });
      toast.success('Default voice updated successfully!');
      handleCancelVoiceEdit();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update voice';
      toast.error(errorMessage);
    }
  };

  /**
   * Check if changing order would leave the language without a default locale (order=1).
   * Returns true if the change would remove the only order=1 locale for a language.
   */
  const willRemoveDefaultLocale = (
    localeCode: string,
    originalOrder: number,
    newOrder: number
  ): boolean => {
    if (originalOrder !== 1 || newOrder === 1) {
      // Not changing from order=1 to something else, no warning needed
      return false;
    }

    // Find the language for this locale
    const locale = locales?.find((l) => l.localeCode === localeCode);
    if (!locale) return false;

    // Check if there are other locales with order=1 for this language
    const otherOrder1Locales = locales?.filter(
      (l) => l.languageCode === locale.languageCode && l.localeCode !== localeCode && l.order === 1
    );

    return !otherOrder1Locales || otherOrder1Locales.length === 0;
  };

  const handleSave = async (localeCode: string) => {
    const request: UpdateLocaleRequest = {};
    const originalLocale = locales?.find((l) => l.localeCode === localeCode);

    if (originalLocale) {
      if (editForm.name !== originalLocale.name) {
        request.name = editForm.name;
      }
      if (editForm.order !== originalLocale.order) {
        request.order = editForm.order;
      }
    }

    if (Object.keys(request).length === 0) {
      handleCancelEdit();
      return;
    }

    // Warn if changing order would leave language without a default locale
    if (
      originalLocale &&
      request.order !== undefined &&
      willRemoveDefaultLocale(localeCode, originalLocale.order, request.order)
    ) {
      const confirmChange = window.confirm(
        `Warning: Changing the order from 1 will leave "${originalLocale.languageCode}" without a default locale.\n\n` +
          `The locale with order=1 is used as the default for this language. ` +
          `You should assign order=1 to another ${originalLocale.languageCode} locale.\n\n` +
          `Do you want to continue?`
      );
      if (!confirmChange) {
        return;
      }
    }

    try {
      await updateLocaleMutation.mutateAsync({ localeCode, request });
      toast.success('Locale updated successfully!');
      handleCancelEdit();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update locale';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (localeCode: string, voiceCount: number) => {
    if (voiceCount > 0) {
      toast.error(`Cannot delete locale - ${voiceCount} voice(s) are using it`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete locale "${localeCode}"?`)) {
      return;
    }

    try {
      await deleteLocaleMutation.mutateAsync(localeCode);
      toast.success('Locale deleted successfully!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete locale';
      toast.error(errorMessage);
    }
  };

  const handleCreate = async () => {
    // Validate locale code format (BCP 47: xx-XX)
    const localePattern = /^[a-z]{2}-[A-Z]{2}$/;
    if (!localePattern.test(createForm.localeCode)) {
      toast.error('Locale code must be in BCP 47 format (e.g., en-US, es-MX)');
      return;
    }

    if (!createForm.languageCode) {
      toast.error('Please select a language');
      return;
    }

    if (!createForm.name.trim()) {
      toast.error('Please enter a locale name');
      return;
    }

    const request: CreateLocaleRequest = {
      localeCode: createForm.localeCode,
      languageCode: createForm.languageCode,
      name: createForm.name.trim(),
      order: createForm.order,
    };

    try {
      await createLocaleMutation.mutateAsync(request);
      toast.success('Locale created successfully!');
      setIsCreating(false);
      setCreateForm({ localeCode: '', languageCode: '', name: '', order: 1 });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create locale';
      toast.error(errorMessage);
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setCreateForm({ localeCode: '', languageCode: '', name: '', order: 1 });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading locales</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  const displayLocales = locales || [];
  const targetLanguages = languages?.filter((l) => l.supportedAsTarget) || [];

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Language Locales</h1>
          <p className="text-gray-600 mt-1">
            Manage BCP 47 locale codes and default voices for regional accent classification
          </p>
        </div>
        <button
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-locale-button"
        >
          Add Locale
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="create-locale-form">
          <h2 className="text-xl font-semibold mb-4">Create New Locale</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Locale Code (BCP 47)
              </label>
              <input
                type="text"
                value={createForm.localeCode}
                onChange={(e) => setCreateForm({ ...createForm, localeCode: e.target.value })}
                placeholder="e.g., en-US, es-MX"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="locale-code-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: language-REGION (e.g., en-US for American English)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                value={createForm.languageCode}
                onChange={(e) => setCreateForm({ ...createForm, languageCode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="language-select"
              >
                <option value="">-- Select Language --</option>
                {targetLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name} ({lang.code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g., American English"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="locale-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                value={createForm.order}
                onChange={(e) =>
                  setCreateForm({ ...createForm, order: parseInt(e.target.value, 10) || 1 })
                }
                min={1}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="locale-order-input"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={handleCancelCreate}
              disabled={createLocaleMutation.isPending}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="cancel-create-button"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={createLocaleMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              data-testid="submit-create-button"
            >
              {createLocaleMutation.isPending ? 'Creating...' : 'Create Locale'}
            </button>
          </div>
        </div>
      )}

      {/* Locales Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="locales-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Locale Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Language
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Default Voice
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Voices
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayLocales.map((locale) => {
              const localeVoice = getDefaultVoice(locale.localeCode);
              return (
                <tr key={locale.localeCode} data-testid={`locale-row-${locale.localeCode}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 font-mono">
                      {locale.localeCode}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{locale.languageCode}</div>
                  </td>
                  <td className="px-6 py-4">
                    {editingLocale === locale.localeCode ? (
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="edit-name-input"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{locale.name}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingLocale === locale.localeCode ? (
                      <input
                        type="number"
                        value={editForm.order}
                        onChange={(e) =>
                          setEditForm({ ...editForm, order: parseInt(e.target.value, 10) || 1 })
                        }
                        min={1}
                        className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="edit-order-input"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">{locale.order}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingVoiceLocale === locale.localeCode ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedVoiceId}
                          onChange={(e) => setSelectedVoiceId(e.target.value)}
                          disabled={isLoadingVoices}
                          className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="voice-selector"
                        >
                          <option value="">-- No voice --</option>
                          {availableVoices?.map((voice) => (
                            <option key={voice.id} value={voice.id}>
                              {voice.name} ({voice.providerName}, {voice.gender})
                            </option>
                          ))}
                        </select>
                        {isLoadingVoices && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        )}
                        <button
                          onClick={() => handleSaveVoice(locale.localeCode)}
                          disabled={setDefaultVoiceMutation.isPending}
                          className="text-blue-600 hover:text-blue-800 text-sm disabled:opacity-50"
                          data-testid="save-voice-button"
                        >
                          {setDefaultVoiceMutation.isPending ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelVoiceEdit}
                          disabled={setDefaultVoiceMutation.isPending}
                          className="text-gray-600 hover:text-gray-800 text-sm disabled:opacity-50"
                          data-testid="cancel-voice-button"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : localeVoice?.defaultVoice ? (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {localeVoice.defaultVoice.name}
                        </span>
                        <button
                          onClick={() => handleEditVoiceClick(locale)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                          data-testid="change-voice-button"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Not configured
                        </span>
                        <button
                          onClick={() => handleEditVoiceClick(locale)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                          data-testid="configure-voice-button"
                        >
                          Configure
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        locale.voiceCount > 0
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {locale.voiceCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {editingLocale === locale.localeCode ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSave(locale.localeCode)}
                          disabled={updateLocaleMutation.isPending}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                          data-testid="save-button"
                        >
                          {updateLocaleMutation.isPending ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={updateLocaleMutation.isPending}
                          className="text-gray-600 hover:text-gray-800 disabled:opacity-50"
                          data-testid="cancel-button"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleEditClick(locale)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid="edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(locale.localeCode, locale.voiceCount)}
                          disabled={
                            deleteLocaleMutation.isPending ||
                            locale.voiceCount > 0 ||
                            locale.isDefault
                          }
                          className={`${
                            locale.voiceCount > 0 || locale.isDefault
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-800'
                          } disabled:opacity-50`}
                          title={
                            locale.isDefault
                              ? 'Cannot delete - default locale'
                              : locale.voiceCount > 0
                                ? 'Cannot delete - locale in use'
                                : 'Delete'
                          }
                          data-testid="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {displayLocales.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg">No locales configured in the system.</p>
          <p className="text-gray-500 mt-2">Click "Add Locale" to create the first one.</p>
        </div>
      )}
    </div>
  );
}
